/**
 * Fertilizer Schedule Calculator engine — pure functions, no side effects.
 *
 * Given a named product, ZIP, lawn size, and grass type, it computes:
 *  - the amount of product per application (lbs or fl oz), bags/bottles, and the
 *    actual nitrogen delivered per 1,000 sq ft (the comparison-shopping number),
 *  - a region- and grass-tuned annual application schedule (windows + which one
 *    is next), and
 *  - agronomic cautions (nitrogen cap, weed & feed / pre-emergent / starter rules).
 *
 * Region resolution reuses the site-wide getRegionFromZipCode + REGION_TUNING so
 * the same ZIP maps to the same region everywhere. The per-application weight is
 * driven by each product's LABEL rate (not a target-N back-calculation), which is
 * the agronomically correct source of truth — a 1-0-1 iron supplement and a 32-0-4
 * lawn food are both applied at their printed rate, independent of nitrogen.
 */

import { getRegionFromZipCode } from '@/data/regional-climate'
import {
  getFertilizerProduct,
  getGrassOption,
  REGION_TUNING,
  MONTH_ABBR,
  type FertilizerProduct,
  type GrassOption,
} from '@/data/fertilizer-products'

/** Max actual nitrogen per 1,000 sq ft per year most lawns should get. */
export const ANNUAL_N_CAP = 4

export interface ScheduleInput {
  productId: string
  zipCode: string
  areaSqFt: number
  grassId: string
  /** Injected "current date" for deterministic tests. Defaults to real now. */
  now?: Date
}

export interface ScheduleApplication {
  /** 1-based order in the season. */
  n: number
  /** Human window, e.g. "Late May" or "Whenever you seed or sod". */
  window: string
  /** The anchor's descriptive note, e.g. "Around Memorial Day". */
  note: string
  /** Fractional month (1-12) after region shift; 0 for starter "any time". */
  month: number
  /** 0-based month index for the calendar strip, or -1 for starter. */
  monthIndex: number
  isNext: boolean
}

export interface MonthCell {
  label: string
  monthIndex: number
  hasApp: boolean
  isNext: boolean
}

export interface ScheduleResult {
  product: FertilizerProduct
  grass: GrassOption | null
  region: string | null
  regionKnown: boolean
  /** Per-application amount. */
  perApp: {
    /** Primary amount value, e.g. "64" (lbs) or "50" (fl oz). */
    amount: string
    /** Unit for the amount, "lbs" or "fl oz". */
    unit: string
    /** Bag/bottle count for one application (>= 1). */
    containers: number
    /** "bag"/"bags" or "bottle"/"bottles". */
    containerLabel: string
    /** Label rate string, e.g. "12.8 lbs". */
    rateLabel: string
    /** Actual N delivered per 1,000 sq ft this application. */
    nPer1000: number
    nPer1000Label: string
  }
  applications: ScheduleApplication[]
  monthCells: MonthCell[]
  appsPerYear: number
  /** Annual container total, e.g. "2 bags / yr" or "3 bottles / yr". */
  annualContainersLabel: string
  nextApplication: ScheduleApplication | null
  cautions: string[]
}

const clampMonth = (m: number) => Math.max(2.5, Math.min(11.8, m))
const LIQUID_LBS_PER_FL_OZ = 8.34 / 128

/** "Early/Mid/Late <Month>" from a fractional month (1-12). */
export function monthWord(m: number): string {
  const idx = Math.max(0, Math.min(11, Math.floor(m - 1)))
  const frac = m - Math.floor(m)
  const part = frac < 0.34 ? 'Early' : frac < 0.67 ? 'Mid' : 'Late'
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]
  return `${part} ${months[idx]}`
}

export function calculateFertilizerSchedule(input: ScheduleInput): ScheduleResult | null {
  const product = getFertilizerProduct(input.productId)
  if (!product) return null

  const grass = getGrassOption(input.grassId) ?? null
  const region = getRegionFromZipCode(input.zipCode)
  const regionKnown = region !== 'Unknown'
  const tuning = regionKnown ? REGION_TUNING[region] ?? { shift: 0, hot: false } : { shift: 0, hot: false }
  const area = Number.isFinite(input.areaSqFt) ? Math.max(0, input.areaSqFt) : 0
  const now = input.now ?? new Date()
  const nowMonth = now.getMonth() + 1 + now.getDate() / 31

  const nPct = product.npkParts.n / 100
  // Liquid labels give fluid ounces, while N-P-K is percent by weight. Use a
  // water-density proxy until product-specific density is captured.
  const nPer1000 =
    product.form === 'liquid'
      ? product.labelRatePer1000 * LIQUID_LBS_PER_FL_OZ * nPct
      : product.labelRatePer1000 * nPct

  const cautions: string[] = []

  // Region-shift the base anchors, then filter by grass activity.
  let anchors = product.anchors.map((a) => ({
    m: a.m === 0 ? 0 : clampMonth(a.m + tuning.shift),
    baseM: a.m,
    label: a.label,
  }))

  if (grass?.type === 'cool' && tuning.hot) {
    const before = anchors.length
    anchors = anchors.filter((a) => a.m === 0 || a.m < 6.2 || a.m > 8.6)
    if (anchors.length < before) {
      cautions.push(
        'Summer applications dropped: cool-season lawns in hot regions should not get summer nitrogen.',
      )
    }
  }
  if (grass?.type === 'warm') {
    const before = anchors.length
    anchors = anchors.filter((a) => a.baseM === 0 || a.baseM < 10.2)
    if (anchors.length < before) {
      cautions.push(
        "Late-fall application dropped: warm-season grass is heading into dormancy and can't use it.",
      )
    }
  }

  anchors = anchors.slice(0, product.maxAppsPerYear)

  // Annual nitrogen cap: trim applications if the product would exceed 4 lb N/1,000/yr.
  if (!product.starterOnly && anchors.length * nPer1000 > ANNUAL_N_CAP) {
    anchors = anchors.slice(0, Math.max(1, Math.floor(ANNUAL_N_CAP / nPer1000)))
    cautions.push(
      `Schedule trimmed to stay under ${ANNUAL_N_CAP} lbs of nitrogen per 1,000 sq ft per year.`,
    )
  }

  if (product.containsWeedControl && !product.containsPreEmergent) {
    cautions.push(
      'Apply to damp grass so granules stick to weed leaves, and wait 4 weeks before overseeding.',
    )
  }
  if (product.containsPreEmergent) {
    cautions.push(
      'Spring timing is strict: the pre-emergent must go down before crabgrass germinates (soil 55°F). No seeding for about 4 months after.',
    )
  }
  if (product.starterOnly) {
    cautions.push(
      'Starter food is a one-time application at seeding or sodding. Switch to a regular fertilizer after 6-8 weeks.',
    )
  }

  // Which application is next, relative to "now".
  const nextIdx = product.starterOnly
    ? 0
    : (() => {
        // "Next" must mean a future application. A half-month grace period
        // kept early-July anchors labeled as next well after they had passed.
        const i = anchors.findIndex((a) => a.m >= nowMonth)
        return i === -1 ? 0 : i
      })()

  const applications: ScheduleApplication[] = anchors.map((a, i) => ({
    n: i + 1,
    window: a.m === 0 ? 'Whenever you seed or sod' : monthWord(a.m),
    note: a.label,
    month: a.m,
    monthIndex: a.m > 0 ? Math.floor(a.m) - 1 : -1,
    isNext: i === nextIdx && !product.starterOnly,
  }))

  const nextApplication = applications[nextIdx] ?? null
  const appMonthIndexes = new Set(applications.filter((a) => a.monthIndex >= 0).map((a) => a.monthIndex))
  const nextMonthIndex = !product.starterOnly && nextApplication ? nextApplication.monthIndex : -1

  const monthCells: MonthCell[] = MONTH_ABBR.map((label, i) => ({
    label: label.charAt(0),
    monthIndex: i,
    hasApp: appMonthIndexes.has(i),
    isNext: i === nextMonthIndex,
  }))

  // Per-application amount. Whole numbers render without a trailing ".0".
  const rounded = (v: number) => (Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1))
  let perApp: ScheduleResult['perApp']
  if (product.form === 'liquid') {
    const containerOz = product.containerOz ?? 32
    const totalOz = (product.labelRatePer1000 * area) / 1000
    const bottles = Math.max(1, Math.ceil(totalOz / containerOz))
    perApp = {
      amount: totalOz.toFixed(0),
      unit: 'fl oz',
      containers: bottles,
      containerLabel: bottles === 1 ? 'bottle' : 'bottles',
      rateLabel: product.rateLabel,
      nPer1000,
      nPer1000Label: nPer1000.toFixed(2),
    }
  } else {
    const bagLbs = product.bagLbs ?? 50
    const totalLbs = (product.labelRatePer1000 * area) / 1000
    const bags = Math.max(1, Math.ceil(totalLbs / bagLbs))
    perApp = {
      amount: rounded(totalLbs),
      unit: 'lbs',
      containers: bags,
      containerLabel: bags === 1 ? 'bag' : 'bags',
      rateLabel: product.rateLabel,
      nPer1000,
      nPer1000Label: nPer1000.toFixed(2),
    }
  }

  const appsPerYear = applications.length
  const annualContainers = perApp.containers * appsPerYear
  const annualUnit =
    product.form === 'liquid'
      ? annualContainers === 1 ? 'bottle' : 'bottles'
      : annualContainers === 1 ? 'bag' : 'bags'
  const annualContainersLabel = `${annualContainers} ${annualUnit} / yr`

  return {
    product,
    grass,
    region: regionKnown ? region : null,
    regionKnown,
    perApp,
    applications,
    monthCells,
    appsPerYear,
    annualContainersLabel,
    nextApplication,
    cautions,
  }
}
